<!--
+------------------------------------------------------------------+
| Author: Jeffrey Thümann                                          |
| Component: ReconstructedAncestorsOverview                         |
| Section: components                                              |
| Purpose: Evidence-first reconstructed ancestor DNA overview.      |
+------------------------------------------------------------------+
-->
<template>
    <article class="reconstructed-overview">
        <p v-if="loading" class="state-message">{{ $t('geneticMatches.chromosomeBrowser.reconstructed.loading') }}</p>
        <p v-else-if="error" class="state-message state-message--error">{{ error }}</p>
        <p v-else-if="ancestors.length === 0" class="state-message">
            {{ $t('geneticMatches.chromosomeBrowser.reconstructed.noAncestors') }}
        </p>

        <template v-else>
            <h2 v-if="selectedAncestor" class="reconstructed-overview-title">
                {{ $t('geneticMatches.chromosomeBrowser.reconstructed.profileTitle', {
                    ancestor: selectedAncestor.ancestorLabel
                }) }}
            </h2>

            <div class="reconstructed-metrics">
                <div class="reconstructed-metric-card" :class="{ 'is-animating': metricsAnimating }">
                    <span>{{ $t('geneticMatches.chromosomeBrowser.reconstructed.metrics.cm') }}</span>
                    <strong>{{ formatOneDecimal(tweenedTotals.assignedCentimorgans) }}</strong>
                </div>
                <div class="reconstructed-metric-card" :class="{ 'is-animating': metricsAnimating }">
                    <span>{{ $t('geneticMatches.chromosomeBrowser.reconstructed.metrics.segments') }}</span>
                    <strong>{{ formatInteger(tweenedTotals.includedSegmentCount) }}</strong>
                </div>
                <div class="reconstructed-metric-card" :class="{ 'is-animating': metricsAnimating }">
                    <span>{{ $t('geneticMatches.chromosomeBrowser.reconstructed.metrics.matches') }}</span>
                    <strong>{{ formatInteger(tweenedTotals.matchCount) }}</strong>
                </div>
            </div>

            <div class="reconstructed-detail-layout">
                <div class="ancestor-grid">
                    <article v-for="ancestor in ancestors" :key="ancestor.ancestorKey" class="ancestor-card">
                        <header class="ancestor-card-heading">
                            <strong :title="ancestor.ancestorLabel">
                                {{ $t('geneticMatches.chromosomeBrowser.reconstructed.cardTitle', {
                                    ancestor: ancestor.ancestorLabel
                                }) }}
                            </strong>
                        </header>

                        <div class="fingerprint-frame" aria-hidden="true">
                            <svg class="fingerprint-svg" viewBox="0 0 216 216" role="img">
                                <g v-for="ring in fingerprintRings(ancestor)" :key="`${ancestor.ancestorKey}-ring-${ring.number}`"
                                    class="fingerprint-ring">
                                    <circle class="fingerprint-ring-base" cx="108" cy="108" :r="ring.radius"
                                        :stroke-width="ring.strokeWidth" />
                                    <path v-for="segment in ring.segments"
                                        :key="`${ring.number}-evidence-${segment.index}`"
                                        class="fingerprint-segment"
                                        :d="arcPath(108, 108, ring.radius, segment.startAngle, segment.endAngle)"
                                        :stroke-width="ring.strokeWidth" />
                                    <path v-for="segment in ring.lineSegments"
                                        :key="`${ring.number}-line-${segment.attributionIndex}-${segment.index}`"
                                        class="fingerprint-line-segment"
                                        :style="{ stroke: segment.color }"
                                        :d="arcPath(108, 108, ring.radius, segment.startAngle, segment.endAngle)"
                                        :stroke-width="ring.lineStrokeWidth" />
                                </g>
                            </svg>
                        </div>

                        <p class="assigned-coverage-summary">
                            {{ $t('geneticMatches.chromosomeBrowser.reconstructed.assignedCoverage', {
                                cm: formatOneDecimal(ancestor.assignedCentimorgans),
                                percent: formatOneDecimal(assignedGenomePercentage(ancestor))
                            }) }}
                        </p>

                        <div v-if="lineAttributionLegendItems(ancestor).length > 0" class="line-attribution-legend">
                            <strong>{{ $t('geneticMatches.chromosomeBrowser.reconstructed.lineAttribution.title') }}</strong>
                            <div class="line-attribution-row">
                                <span class="line-attribution-marker line-attribution-marker--base" />
                                <span>{{ $t('geneticMatches.chromosomeBrowser.reconstructed.lineAttribution.pairEvidence') }}</span>
                                <b>{{ formatOneDecimal(ancestor.assignedCentimorgans) }} cM</b>
                            </div>
                            <div v-for="item in lineAttributionLegendItems(ancestor)" :key="item.ancestorPersonID"
                                class="line-attribution-row">
                                <span class="line-attribution-marker" :style="{ background: item.color }" />
                                <span>{{ item.ancestorLabel }}</span>
                                <b>{{ formatOneDecimal(item.assignedCentimorgans) }} cM</b>
                            </div>
                        </div>
                    </article>
                </div>

                <section v-if="selectedAncestor" class="evidence-panel">
                    <header>
                        <div>
                            <h3>{{ $t('geneticMatches.chromosomeBrowser.reconstructed.evidence.panelTitle') }}</h3>
                        </div>
                    </header>

                    <div class="evidence-table-wrap">
                        <table class="evidence-table">
                            <thead>
                                <tr>
                                    <th>{{ $t('geneticMatches.chromosomeBrowser.reconstructed.evidence.tester') }}</th>
                                    <th>{{ $t('geneticMatches.chromosomeBrowser.reconstructed.evidence.match') }}</th>
                                    <th>{{ $t('geneticMatches.chromosomeBrowser.reconstructed.evidence.chromosome') }}</th>
                                    <th>{{ $t('geneticMatches.chromosomeBrowser.reconstructed.evidence.range') }}</th>
                                    <th>{{ $t('geneticMatches.chromosomeBrowser.reconstructed.evidence.cm') }}</th>
                                    <th>{{ $t('geneticMatches.chromosomeBrowser.reconstructed.evidence.snps') }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="segment in selectedAncestor.evidence" :key="segment.id">
                                    <td>{{ segment.testerCategory }}</td>
                                    <td>{{ segment.matchName }}</td>
                                    <td>{{ segment.chromosome }}</td>
                                    <td>{{ formatOneDecimal(segment.startMb) }}-{{ formatOneDecimal(segment.endMb) }} Mb</td>
                                    <td>{{ formatOneDecimal(segment.centimorgans) }}</td>
                                    <td>{{ formatInteger(segment.snps) }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </section>
            </div>
        </template>
    </article>
</template>

<script>
import { CHROMOSOME_SIZES_HG19 } from '@/data/dna_segments/chromosomeSizesHg19';

export default {
    name: 'ReconstructedAncestorsOverview',
    props: {
        ancestors: {
            type: Array,
            default: () => [],
        },
        totals: {
            type: Object,
            default: () => ({}),
        },
        loading: {
            type: Boolean,
            default: false,
        },
        error: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            tweenedTotals: {
                reconstructedAncestorCount: 0,
                assignedCentimorgans: 0,
                includedSegmentCount: 0,
                matchCount: 0,
            },
            metricsAnimationFrame: null,
            metricsAnimating: false,
            metricsInitialized: false,
        };
    },
    computed: {
        selectedAncestor() {
            return this.ancestors[0] || null;
        },
    },
    watch: {
        totals: {
            immediate: true,
            deep: true,
            handler(nextTotals) {
                this.animateMetrics(nextTotals);
            },
        },
        ancestors() {
            this.animateMetrics(this.totals);
        },
    },
    beforeUnmount() {
        if (this.metricsAnimationFrame) {
            cancelAnimationFrame(this.metricsAnimationFrame);
        }
    },
    methods: {
        formatOneDecimal(value) {
            return Number(value || 0).toFixed(1);
        },
        formatThreeDecimal(value) {
            return Number(value || 0).toFixed(3);
        },
        formatInteger(value) {
            return new Intl.NumberFormat(this.$i18n?.locale?.value || undefined).format(Math.round(value || 0));
        },
        normalizeMetrics(totals = this.totals) {
            const derivedMatchCount = this.ancestors.reduce((sum, ancestor) => sum + Number(ancestor.matchCount || 0), 0);
            return {
                reconstructedAncestorCount: Number(totals?.reconstructedAncestorCount || this.ancestors.length || 0),
                assignedCentimorgans: Number(totals?.assignedCentimorgans || 0),
                includedSegmentCount: Number(totals?.includedSegmentCount || 0),
                matchCount: Number(totals?.matchCount ?? derivedMatchCount),
            };
        },
        easeOutCubic(progress) {
            return 1 - Math.pow(1 - progress, 3);
        },
        animateMetrics(nextTotals) {
            const target = this.normalizeMetrics(nextTotals);

            if (this.metricsAnimationFrame) {
                cancelAnimationFrame(this.metricsAnimationFrame);
            }

            if (!this.metricsInitialized) {
                this.tweenedTotals = { ...target };
                this.metricsInitialized = true;
                return;
            }

            const start = { ...this.tweenedTotals };
            const duration = 480;
            const startedAt = performance.now();
            this.metricsAnimating = true;

            const step = (now) => {
                const progress = Math.min((now - startedAt) / duration, 1);
                const eased = this.easeOutCubic(progress);

                this.tweenedTotals = {
                    reconstructedAncestorCount: start.reconstructedAncestorCount
                        + ((target.reconstructedAncestorCount - start.reconstructedAncestorCount) * eased),
                    assignedCentimorgans: start.assignedCentimorgans
                        + ((target.assignedCentimorgans - start.assignedCentimorgans) * eased),
                    includedSegmentCount: start.includedSegmentCount
                        + ((target.includedSegmentCount - start.includedSegmentCount) * eased),
                    matchCount: start.matchCount
                        + ((target.matchCount - start.matchCount) * eased),
                };

                if (progress < 1) {
                    this.metricsAnimationFrame = requestAnimationFrame(step);
                    return;
                }

                this.tweenedTotals = { ...target };
                this.metricsAnimating = false;
                this.metricsAnimationFrame = null;
            };

            this.metricsAnimationFrame = requestAnimationFrame(step);
        },
        assignedGenomePercentage(ancestor) {
            return Math.min(Math.max(Number(ancestor?.assignedGenomePercentage ?? ancestor?.autosomalPercentage ?? 0), 0), 100);
        },
        lineAttributionLegendItems(ancestor) {
            return Array.isArray(ancestor?.lineSupportedAttributions)
                ? ancestor.lineSupportedAttributions.filter(item => Number(item.assignedCentimorgans || 0) > 0)
                : [];
        },
        fingerprintRings(ancestor) {
            const ringCount = 23;
            const outerRadius = 99;
            const ringSpacing = 4;
            const strokeWidth = 3.1;
            const lineStrokeWidth = 1.5;

            return Array.from({ length: ringCount }, (_, index) => {
                const chromosomeNumber = index + 1;
                const radius = outerRadius - (index * ringSpacing);
                const chromosomeLength = CHROMOSOME_SIZES_HG19[chromosomeNumber] || CHROMOSOME_SIZES_HG19[22] || 1;
                const coverage = (ancestor?.chromosomeCoverage || [])
                    .find(row => Number(row.chromosome) === chromosomeNumber);
                const segments = [];

                (coverage?.intervals || []).forEach((interval, intervalIndex) => {
                    const startBp = Math.max(Number(interval.startBp || 0), 0);
                    const endBp = Math.min(Number(interval.endBp || 0), chromosomeLength);
                    if (endBp <= startBp) {
                        return;
                    }

                    const intervalStartAngle = (startBp / chromosomeLength) * 360;
                    const intervalEndAngle = (endBp / chromosomeLength) * 360;
                    const intervalSpan = Math.max(intervalEndAngle - intervalStartAngle, 0);

                    const gap = intervalSpan > 0.7 ? Math.min(0.35, intervalSpan * 0.18) : 0;
                    const startAngle = intervalStartAngle + (gap / 2);
                    const endAngle = intervalEndAngle - (gap / 2);

                    if (endAngle <= startAngle) {
                        return;
                    }

                    segments.push({
                        index: intervalIndex,
                        startAngle,
                        endAngle,
                    });
                });

                const lineSegments = [];
                this.lineAttributionLegendItems(ancestor).forEach((attribution, attributionIndex) => {
                    const attributionCoverage = (attribution.intervalsByChromosome || [])
                        .find(row => Number(row.chromosome) === chromosomeNumber);

                    (attributionCoverage?.intervals || []).forEach((interval, intervalIndex) => {
                        const startBp = Math.max(Number(interval.startBp || 0), 0);
                        const endBp = Math.min(Number(interval.endBp || 0), chromosomeLength);
                        if (endBp <= startBp) {
                            return;
                        }

                        const intervalStartAngle = (startBp / chromosomeLength) * 360;
                        const intervalEndAngle = (endBp / chromosomeLength) * 360;
                        const intervalSpan = Math.max(intervalEndAngle - intervalStartAngle, 0);
                        const gap = intervalSpan > 0.7 ? Math.min(0.35, intervalSpan * 0.18) : 0;
                        const startAngle = intervalStartAngle + (gap / 2);
                        const endAngle = intervalEndAngle - (gap / 2);

                        if (endAngle <= startAngle) {
                            return;
                        }

                        lineSegments.push({
                            index: intervalIndex,
                            attributionIndex,
                            color: attribution.color,
                            startAngle,
                            endAngle,
                        });
                    });
                });

                return {
                    number: chromosomeNumber,
                    radius,
                    strokeWidth,
                    lineStrokeWidth,
                    segments,
                    lineSegments,
                };
            });
        },
        polarToCartesian(cx, cy, radius, angleDegrees) {
            const angleRadians = (angleDegrees - 90) * Math.PI / 180;
            return {
                x: cx + (radius * Math.cos(angleRadians)),
                y: cy + (radius * Math.sin(angleRadians)),
            };
        },
        arcPath(cx, cy, radius, startAngle, endAngle) {
            const normalizedStart = ((startAngle % 360) + 360) % 360;
            const normalizedEnd = normalizedStart + Math.max(endAngle - startAngle, 0.1);
            const start = this.polarToCartesian(cx, cy, radius, normalizedEnd);
            const end = this.polarToCartesian(cx, cy, radius, normalizedStart);
            const largeArcFlag = normalizedEnd - normalizedStart <= 180 ? '0' : '1';

            return [
                'M', start.x.toFixed(3), start.y.toFixed(3),
                'A', radius, radius, 0, largeArcFlag, 0, end.x.toFixed(3), end.y.toFixed(3),
            ].join(' ');
        },
    },
};
</script>

<style scoped>
.reconstructed-overview {
    min-width: 0;
}

.reconstructed-overview-title {
    color: var(--brand-text-dark);
    font-size: 30px;
    line-height: 1.15;
    margin: 0 0 18px;
}

.state-message {
    color: #777;
    padding: 48px 0;
    text-align: center;
}

.state-message--error {
    color: var(--brand-color-1);
}

.evidence-panel h3,
.evidence-panel p {
    margin: 0;
}

.evidence-panel p {
    color: #686868;
    line-height: 1.5;
}

.reconstructed-metrics {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(110px, 1fr));
    gap: 10px;
    margin-bottom: 18px;
    width: 100%;
}

.reconstructed-metric-card {
    display: flex;
    flex-direction: column;
    gap: 4px;
    border: 1px solid #ececec;
    border-radius: var(--border-radius-lg);
    background: #fff;
    padding: 10px 12px;
    color: #777;
    font-size: 12px;
    transition: transform 280ms ease, box-shadow 280ms ease, border-color 280ms ease;
}

.reconstructed-metric-card span {
    display: block;
}

.reconstructed-metric-card strong {
    display: block;
    color: var(--brand-text-dark);
    font-size: 18px;
    line-height: 1.2;
}

.reconstructed-metric-card.is-animating {
    transform: translateY(-2px);
    border-color: color-mix(in srgb, var(--brand-color-1) 30%, #ececec);
    box-shadow: 0 10px 24px rgba(174, 30, 37, 0.08);
}

.reconstructed-detail-layout {
    align-items: start;
    display: grid;
    grid-template-columns: minmax(280px, 340px) minmax(0, 1fr);
    gap: 16px;
    min-width: 0;
}

.ancestor-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;
    min-width: 0;
}

.ancestor-card {
    border: 1px solid #e5e5e5;
    border-radius: var(--border-radius-lg);
    background: #fff;
    color: var(--brand-text-dark);
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-height: 360px;
    min-width: 0;
    overflow: hidden;
    padding: 14px;
    text-align: left;
}

.ancestor-card-heading {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 8px;
    align-items: start;
}

.ancestor-card-heading strong {
    line-height: 1.25;
    overflow: visible;
    text-overflow: clip;
    white-space: normal;
}

.fingerprint-frame {
    align-items: center;
    aspect-ratio: 1;
    display: flex;
    justify-content: center;
    margin: 2px auto 0;
    max-width: 100%;
    min-width: 0;
    overflow: hidden;
    width: 100%;
}

.fingerprint-svg {
    display: block;
    height: 100%;
    max-width: 100%;
    overflow: hidden;
    width: 100%;
}

.fingerprint-ring-base,
.fingerprint-segment,
.fingerprint-line-segment {
    fill: none;
}

.fingerprint-ring-base {
    stroke: #efefef;
}

.fingerprint-segment {
    stroke: var(--brand-color-1);
}

.fingerprint-line-segment {
    opacity: 0.95;
}

.assigned-coverage-summary {
    color: #777;
    font-size: 12px;
    line-height: 1.4;
    margin: -2px 0 0;
    text-align: center;
}

.line-attribution-legend {
    border-top: 1px solid #eeeeee;
    display: grid;
    gap: 8px;
    margin-top: 2px;
    padding-top: 10px;
}

.line-attribution-legend > strong {
    color: #555;
    font-size: 12px;
    font-weight: 700;
}

.line-attribution-row {
    align-items: center;
    color: #666;
    display: grid;
    font-size: 12px;
    gap: 8px;
    grid-template-columns: 10px minmax(0, 1fr) auto;
    min-width: 0;
}

.line-attribution-row span:nth-child(2) {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.line-attribution-row b {
    color: var(--brand-text-dark);
    font-weight: 700;
    white-space: nowrap;
}

.line-attribution-marker {
    border-radius: 50%;
    display: block;
    height: 10px;
    width: 10px;
}

.line-attribution-marker--base {
    background: var(--brand-color-1);
}

.evidence-panel {
    border: 1px solid #e5e5e5;
    border-radius: var(--border-radius-lg);
    background: #fff;
    margin-top: 0;
    min-width: 0;
    padding: 16px;
}

.evidence-panel header {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 14px;
}

.evidence-table-wrap {
    min-width: 0;
    overflow-x: auto;
}

.evidence-table {
    border-collapse: collapse;
    width: 100%;
    min-width: 680px;
}

.evidence-table th,
.evidence-table td {
    border-bottom: 1px solid #eeeeee;
    padding: 8px;
    text-align: left;
    white-space: nowrap;
}

.evidence-table th {
    color: #777;
    font-size: 12px;
}

.evidence-table td {
    font-size: 13px;
}

@media (max-width: 860px) {
    .reconstructed-overview-title {
        font-size: 24px;
    }

    .reconstructed-metrics {
        grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    }

    .ancestor-grid {
        grid-template-columns: 1fr;
    }

    .reconstructed-detail-layout {
        grid-template-columns: 1fr;
    }

    .evidence-panel header {
        flex-direction: column;
    }
}
</style>
